import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Wallet, ChevronLeft, ChevronRight, ChevronDown, Search, RefreshCw, X, Check,
  Clock, CalendarPlus, Pencil, Trash2, ArrowUpDown, FolderOpen, FileText, KeyRound, Ban
} from 'lucide-react';
import { billingAPI } from '../api';
import './Subscriptions.css';

const CURRENCY = 'ТМТ';

const STATUS = {
  expired:  { label: 'Просрочено', cls: 'st-expired' },
  expiring: { label: 'Истекает',   cls: 'st-expiring' },
  active:   { label: 'Активна',    cls: 'st-active' },
  'no-term':{ label: 'Без срока',  cls: 'st-noterm' },
  disabled: { label: 'Выключена',  cls: 'st-disabled' },
};

// Сортировка по умолчанию — «кому пора платить первым»: просроченные, затем истекающие.
const STATUS_ORDER = { expired: 0, expiring: 1, 'no-term': 2, active: 3, disabled: 4 };

const FILTERS = [
  { key: 'all',      label: 'Все' },
  { key: 'expired',  label: 'Просрочено' },
  { key: 'expiring', label: 'Истекает' },
  { key: 'no-term',  label: 'Без срока' },
  { key: 'active',   label: 'Активные' },
];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const fmtMoney = (n) => (n === null || n === undefined || n === '') ? '—' : Number(n).toLocaleString('ru-RU');
const shortName = (f) => f.replace(/\.json$/, '');
const shortKey = (k) => k.slice(0, 8);

/**
 * Esc закрывает модалку, Ctrl/⌘+Enter — подтверждает.
 * Enter без модификатора не трогаем: в модалке платежа фокус обычно в поле суммы,
 * и случайное нажатие записывало бы платёж раньше, чем выбран период.
 */
function useModalKeys(onClose, onSubmit) {
  // Колбэки держим в ref: они пересоздаются на каждый рендер, а переподписка слушателя
  // и повторное трогание body.style.overflow на каждый ввод символа ни к чему.
  const cbs = useRef({ onClose, onSubmit });
  cbs.current = { onClose, onSubmit };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cbs.current.onClose(); }
      else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); cbs.current.onSubmit(); }
    };
    window.addEventListener('keydown', handler);
    // Фон под модалкой не должен скроллиться — иначе колесо уводит страницу, а не список платежей.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, []);
}

function daysText(row) {
  if (row.status === 'disabled') return 'выключена';
  if (row.daysLeft === null) return 'не задан';
  if (row.daysLeft < 0) return `${row.daysLeft} дн.`;
  if (row.daysLeft === 0) return 'сегодня';
  return `${row.daysLeft} дн.`;
}

/* ── Модалка платежа по КЛЮЧУ ── */
function PayModal({ row, onPay, onClose }) {
  const [amount, setAmount] = useState(row.price ?? '');
  const [days, setDays] = useState(30);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const base = row.endDate && new Date(row.endDate) > new Date() ? new Date(row.endDate) : new Date();
  const preview = new Date(base);
  preview.setDate(preview.getDate() + (Number(days) || 0));

  const submit = async () => {
    if (amount === '' || saving) return;
    setSaving(true);
    try {
      await onPay({ amount: Number(amount), days: Number(days) || 30, note });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  useModalKeys(onClose, submit);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card sub-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-card-header">
          <span className="modal-card-title">
            <Wallet size={16} /> Платёж — {row.label || shortKey(row.key)}
          </span>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}><X size={15} /></button>
        </div>

        <div className="modal-card-body">
          <div className="sub-modal-sub">
            <FileText size={12} /> {shortName(row.filename)}
            <span className="sub-modal-key">ключ {shortKey(row.key)}…</span>
          </div>

          <div className="sub-form-row">
            <div className="form-group">
              <label className="form-group-label">Сумма, {CURRENCY}</label>
              <input className="input" type="number" value={amount} autoFocus
                     onChange={e => setAmount(e.target.value)} placeholder="500" />
            </div>
            <div className="form-group">
              <label className="form-group-label">Период</label>
              <select className="input" value={days} onChange={e => setDays(e.target.value)}>
                <option value={30}>1 месяц (30 дн.)</option>
                <option value={90}>3 месяца (90 дн.)</option>
                <option value={180}>6 месяцев (180 дн.)</option>
                <option value={365}>1 год (365 дн.)</option>
                <option value={7}>7 дней</option>
                <option value={14}>14 дней</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-group-label">Заметка (необязательно)</label>
            <input className="input" value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Например: оплатил наличными" />
          </div>

          <div className="pay-preview">
            <Clock size={13} />
            <span>
              Срок: <b>{fmtDate(row.endDate)}</b> → <b className="pay-preview-new">{fmtDate(preview.toISOString())}</b>
            </span>
          </div>
          {row.status === 'expired' && (
            <div className="pay-hint">Ключ просрочен — отсчёт пойдёт от сегодня, а не от старой даты.</div>
          )}
          {row.inheritedEndDate && (
            <div className="pay-hint">У ключа не было своей даты — он жил по сроку файла. После платежа срок станет личным.</div>
          )}
        </div>

        <div className="modal-card-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || amount === ''}>
            {saving ? <><div className="spinner spinner-inline" /> Сохранение...</> : <><Check size={14} /> Записать платёж</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Модалка карточки КЛЮЧА: цена, контакт, заметка, срок, история ── */
function DetailsModal({ row, onSave, onSetEndDate, onDeletePayment, onClose }) {
  const [price, setPrice] = useState(row.price ?? '');
  const [contact, setContact] = useState(row.contact || '');
  const [note, setNote] = useState(row.note || '');
  const [endDate, setEndDate] = useState(row.ownEndDate ? row.ownEndDate.slice(0, 10) : '');
  const [payments, setPayments] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    billingAPI.getKey(row.filename, row.key)
      .then(res => setPayments(res.data.payments || []))
      .catch(() => setPayments([]));
  }, [row.filename, row.key]);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave({ price: price === '' ? null : Number(price), contact, note });
      const initial = row.ownEndDate ? row.ownEndDate.slice(0, 10) : '';
      if (endDate !== initial) {
        // Дату держим на конце суток, чтобы «до 31-го» означало весь 31-й день.
        await onSetEndDate(endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  useModalKeys(onClose, submit);

  const removePayment = async (id) => {
    if (!confirm('Удалить эту запись о платеже?\nСрок действия ключа при этом не откатится.')) return;
    await onDeletePayment(id);
    setPayments(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card sub-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-card-header">
          <span className="modal-card-title">
            <Pencil size={16} /> {row.label || shortKey(row.key)}
          </span>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}><X size={15} /></button>
        </div>

        <div className="modal-card-body">
          <div className="sub-modal-sub">
            <FileText size={12} /> {shortName(row.filename)}
            <span className="sub-modal-key">ключ {shortKey(row.key)}…</span>
          </div>

          <div className="sub-form-row">
            <div className="form-group">
              <label className="form-group-label">Цена за месяц, {CURRENCY}</label>
              <input className="input" type="number" value={price}
                     onChange={e => setPrice(e.target.value)} placeholder="500" />
            </div>
            <div className="form-group">
              <label className="form-group-label">Контакт</label>
              <input className="input" value={contact}
                     onChange={e => setContact(e.target.value)} placeholder="@telegram / телефон" />
            </div>
          </div>

          <div className="sub-form-row">
            <div className="form-group">
              <label className="form-group-label">Личный срок ключа</label>
              <input className="input" type="date" value={endDate}
                     onChange={e => setEndDate(e.target.value)} />
              <span className="form-hint">
                {row.inheritedEndDate
                  ? `Сейчас пусто — действует срок файла (${fmtDate(row.endDate)})`
                  : 'Пусто = ключ живёт по сроку файла'}
              </span>
            </div>
            <div className="form-group">
              <label className="form-group-label">Заметка</label>
              <input className="input" value={note} onChange={e => setNote(e.target.value)}
                     placeholder="Что угодно для себя" />
            </div>
          </div>

          <div className="sub-history">
            <div className="sub-history-title">История платежей</div>
            {payments === null ? (
              <div className="sub-history-empty"><div className="spinner spinner-inline" /> Загрузка…</div>
            ) : payments.length === 0 ? (
              <div className="sub-history-empty">Платежей пока нет</div>
            ) : (
              <div className="sub-history-list">
                {[...payments].reverse().map(p => (
                  <div key={p.id} className="sub-history-row">
                    <span className="sh-date">{fmtDate(p.date)}</span>
                    <span className="sh-amount">{fmtMoney(p.amount)} {CURRENCY}</span>
                    <span className="sh-days">+{p.days} дн.</span>
                    <span className="sh-note">{p.note}</span>
                    <button className="btn btn-ghost btn-sm btn-icon" title="Удалить запись"
                            onClick={() => removePayment(p.id)}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-card-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? <><div className="spinner spinner-inline" /> Сохранение...</> : <><Check size={14} /> Сохранить</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Страница ── */
function Subscriptions({ onClose }) {
  const [data, setData] = useState({ groups: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ by: 'status', dir: 'asc' });
  const [payRow, setPayRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [collapsedFiles, setCollapsedFiles] = useState(() => new Set());

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await billingAPI.getOverview();
      setData(res.data || { groups: [], totals: null });
      setError('');
    } catch (err) {
      setError('Не удалось загрузить: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async (payload) => {
    await billingAPI.payKey(payRow.filename, payRow.key, payload);
    await load();
  };

  const handleSaveCard = async (payload) => {
    await billingAPI.updateKey(editRow.filename, editRow.key, payload);
    await load();
  };

  const handleSetEndDate = async (iso) => {
    await billingAPI.setKeyEndDate(editRow.filename, editRow.key, iso);
    await load();
  };

  const matchesQuery = (k, file, group, q) =>
    k.label.toLowerCase().includes(q) ||
    k.key.toLowerCase().includes(q) ||
    (k.contact || '').toLowerCase().includes(q) ||
    (k.note || '').toLowerCase().includes(q) ||
    file.filename.toLowerCase().includes(q) ||
    (group.name || '').toLowerCase().includes(q);

  // Фильтр/поиск применяются к КЛЮЧАМ; файлы и группы без подходящих ключей отпадают.
  const tree = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dir = sort.dir === 'asc' ? 1 : -1;

    const sortKeys = (list) => [...list].sort((a, b) => {
      switch (sort.by) {
        case 'name':  return (a.label || a.key).localeCompare(b.label || b.key) * dir;
        case 'price': return ((a.price ?? -1) - (b.price ?? -1)) * dir;
        case 'days':  return ((a.daysLeft ?? 99999) - (b.daysLeft ?? 99999)) * dir;
        case 'paid':  return (a.totalPaid - b.totalPaid) * dir;
        default:      return (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) * dir
                          || ((a.daysLeft ?? 99999) - (b.daysLeft ?? 99999));
      }
    });

    return (data.groups || []).map(g => {
      const files = g.files.map(f => {
        let keys = f.keys;
        if (filter !== 'all') keys = keys.filter(k => k.status === filter);
        if (q) keys = keys.filter(k => matchesQuery(k, f, g, q));
        return { ...f, keys: sortKeys(keys) };
      }).filter(f =>
        // Файл без ключей — это ещё не выданная подписка; в общем списке он обязан
        // быть виден (иначе про него забудут), а под фильтром/поиском по ключам — нет.
        f.keys.length > 0 ||
        (f.keyCount === 0 && filter === 'all' && (!q || f.filename.toLowerCase().includes(q) || (g.name || '').toLowerCase().includes(q)))
      );
      return { ...g, files };
    }).filter(g => g.files.length > 0);
  }, [data.groups, filter, query, sort]);

  const toggleSort = (by) =>
    setSort(prev => prev.by === by ? { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { by, dir: 'asc' });

  const toggleIn = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleGroup = toggleIn(setCollapsedGroups);
  const toggleFile = toggleIn(setCollapsedFiles);

  // При активном поиске/фильтре дерево всегда раскрыто — иначе результат не виден.
  const forceOpen = filter !== 'all' || query.trim() !== '';

  const t = data.totals;
  const allKeys = useMemo(
    () => (data.groups || []).flatMap(g => g.files.flatMap(f => f.keys)),
    [data.groups]
  );
  const counts = useMemo(() => {
    const c = { all: allKeys.length };
    for (const f of FILTERS.slice(1)) c[f.key] = allKeys.filter(k => k.status === f.key).length;
    return c;
  }, [allKeys]);

  return (
    <div className="subs-page">
      <header className="subs-header">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>
          <ChevronLeft size={15} /> Назад
        </button>
        <span className="subs-title"><Wallet size={17} /> Подписки</span>
        <button className="btn btn-ghost btn-sm" onClick={load} title="Обновить">
          <RefreshCw size={14} />
        </button>
      </header>

      {/* Итоги: деньги в месяц, долг, кто скоро отвалится */}
      <div className="subs-stats">
        <div className="stat-tile">
          <span className="stat-label">В месяц</span>
          <span className="stat-value">{t ? fmtMoney(t.monthly) : '—'} <small>{CURRENCY}</small></span>
        </div>
        <div className="stat-tile stat-danger">
          <span className="stat-label">Долг (просрочено)</span>
          <span className="stat-value">{t ? fmtMoney(t.debt) : '—'} <small>{CURRENCY}</small></span>
        </div>
        <div className="stat-tile stat-warning">
          <span className="stat-label">Истекает ≤7 дней</span>
          <span className="stat-value">{t ? t.expiring : '—'}</span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Ключей всего</span>
          <span className="stat-value">{t ? t.keys : '—'} <small>в {t ? t.subscriptions : '—'} файлах</small></span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Собрано всего</span>
          <span className="stat-value">{t ? fmtMoney(t.collectedAllTime) : '—'} <small>{CURRENCY}</small></span>
        </div>
      </div>

      <div className="subs-toolbar">
        <div className="subs-filters">
          {FILTERS.map(f => (
            <button key={f.key}
                    className={`chip${filter === f.key ? ' chip-active' : ''}`}
                    onClick={() => setFilter(f.key)}>
              {f.label}
              <span className="chip-count">{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="subs-search">
          <Search size={13} className="subs-search-icon" />
          <input className="input" placeholder="Поиск по ключу, файлу, группе, контакту..."
                 value={query} onChange={e => setQuery(e.target.value)} />
          {query && <button className="subs-search-clear" onClick={() => setQuery('')}><X size={12} /></button>}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="subs-table-wrap">
        {loading ? (
          <div className="subs-loading"><div className="spinner" /> Загрузка…</div>
        ) : tree.length === 0 ? (
          <div className="subs-empty">Ничего не найдено</div>
        ) : (
          <table className="subs-table subs-tree">
            <thead>
              <tr>
                <th className="th-status"></th>
                <th onClick={() => toggleSort('name')} className="th-sortable th-name">Ключ / клиент <ArrowUpDown size={11} /></th>
                <th onClick={() => toggleSort('days')} className="th-sortable th-days">Истекает <ArrowUpDown size={11} /></th>
                <th onClick={() => toggleSort('price')} className="th-sortable th-num th-price">Цена <ArrowUpDown size={11} /></th>
                <th className="th-num th-paid">Оплачен</th>
                <th onClick={() => toggleSort('paid')} className="th-sortable th-num th-total">Всего <ArrowUpDown size={11} /></th>
                <th className="th-contact">Контакт</th>
                <th className="th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {tree.map(g => {
                const gOpen = forceOpen || !collapsedGroups.has(g.id);
                return [
                  <tr key={`g-${g.id}`} className="tree-row tree-group" onClick={() => toggleGroup(g.id)}>
                    <td className="td-status"></td>
                    <td className="td-name" colSpan={2}>
                      <span className="tree-caret">
                        {gOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </span>
                      <FolderOpen size={14} className="tree-icon" />
                      <span className="tree-title">{g.name}</span>
                      <span className="tree-badge">{g.fileCount} файл. · {g.keyCount} ключ.</span>
                      {g.expired > 0 && <span className="tree-badge badge-danger">{g.expired} просроч.</span>}
                      {g.expiring > 0 && <span className="tree-badge badge-warning">{g.expiring} истекает</span>}
                    </td>
                    <td className="td-num">{g.monthly ? fmtMoney(g.monthly) : '—'}</td>
                    <td className="td-num"></td>
                    <td className="td-num td-muted">{g.totalPaid ? fmtMoney(g.totalPaid) : '—'}</td>
                    <td colSpan={2}></td>
                  </tr>,

                  ...(gOpen ? g.files.flatMap(f => {
                    const fId = f.filename;
                    const fOpen = forceOpen || !collapsedFiles.has(fId);
                    return [
                      <tr key={`f-${fId}`} className="tree-row tree-file" onClick={() => toggleFile(fId)}>
                        <td className="td-status"></td>
                        <td className="td-name" colSpan={2}>
                          <span className="tree-caret tree-indent-1">
                            {fOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </span>
                          <FileText size={13} className="tree-icon" />
                          <span className="tree-title">{shortName(f.filename)}</span>
                          <span className="tree-badge">{f.keyCount}/{f.maxDevices} устр.</span>
                          {f.endDate && (
                            <span className="tree-badge" title="Общий срок файла — потолок для всех ключей">
                              файл до {fmtDate(f.endDate)}
                            </span>
                          )}
                          {f.mode === 'nonactive' && <span className="tree-badge badge-danger">выключен</span>}
                          {f.keyCount === 0 && <span className="tree-badge">ключей нет</span>}
                        </td>
                        <td className="td-num">{f.monthly ? fmtMoney(f.monthly) : '—'}</td>
                        <td className="td-num"></td>
                        <td className="td-num td-muted">{f.totalPaid ? fmtMoney(f.totalPaid) : '—'}</td>
                        <td colSpan={2}></td>
                      </tr>,

                      ...(fOpen ? f.keys.map(k => {
                        const st = STATUS[k.status] || STATUS['no-term'];
                        return (
                          <tr key={k.key} className={`tree-key ${st.cls}`}>
                            <td className="td-status"><span className="status-bar" title={st.label} /></td>
                            <td className="td-name td-key-name">
                              <KeyRound size={12} className="tree-icon tree-indent-2" />
                              <span className="sub-name">{k.label || shortKey(k.key)}</span>
                              {k.blocked && <span className="key-flag" title="Ключ заблокирован (шеринг)"><Ban size={11} /></span>}
                              <span className="sub-meta">{shortKey(k.key)}…</span>
                              {k.note && <span className="sub-note" title={k.note}>{k.note}</span>}
                            </td>
                            <td className="td-days">
                              <span className="days-value">{daysText(k)}</span>
                              <span className="days-date">
                                {fmtDate(k.endDate)}
                                {k.inheritedEndDate && <span className="days-inherit" title="Своей даты нет — срок файла"> (файл)</span>}
                              </span>
                            </td>
                            <td className="td-num">{fmtMoney(k.price)}</td>
                            <td className="td-num td-muted">{k.lastPayment ? fmtDate(k.lastPayment.date) : '—'}</td>
                            <td className="td-num td-muted">{k.totalPaid ? fmtMoney(k.totalPaid) : '—'}</td>
                            <td className="td-contact">{k.contact || <span className="td-muted">—</span>}</td>
                            <td className="td-actions">
                              <button className="btn btn-primary btn-sm" onClick={() => setPayRow(k)} title="Записать платёж и продлить этот ключ">
                                <CalendarPlus size={13} /><span className="btn-label"> Продлить</span>
                              </button>
                              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setEditRow(k)} title="Цена, контакт, срок, история">
                                <Pencil size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      }) : []),
                    ];
                  }) : []),
                ];
              })}
            </tbody>
          </table>
        )}
      </div>

      {payRow && <PayModal row={payRow} onPay={handlePay} onClose={() => setPayRow(null)} />}
      {editRow && (
        <DetailsModal
          row={editRow}
          onSave={handleSaveCard}
          onSetEndDate={handleSetEndDate}
          onDeletePayment={(id) => billingAPI.deleteKeyPayment(editRow.filename, editRow.key, id).then(load)}
          onClose={() => setEditRow(null)}
        />
      )}
    </div>
  );
}

export default Subscriptions;
